import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    Stack,
    Divider,
    Grid,
    Autocomplete
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Search as SearchIcon, Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';
import localidadService from '../services/localidadService';
import provinciaService, { Provincia } from '../services/provinciaService';
import { useSnackbar } from 'notistack';
import { searchableSelectAutocompleteProps } from './SearchableSelect';

interface Localidad {
    codigoCiudad: number;
    nombreCiudad?: string;
    codigoProvincia?: number;
    codigoRuta?: number;
    codigoPostal?: string;
    nombreProvincia?: string;
}

interface LocalidadSearchProps {
    open: boolean;
    onClose: () => void;
    onSelect: (localidad: Localidad) => void;
}

const LocalidadSearch: React.FC<LocalidadSearchProps> = ({ open, onClose, onSelect }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { enqueueSnackbar } = useSnackbar();
    const [searchTerm, setSearchTerm] = useState('');
    const [localidades, setLocalidades] = useState<Localidad[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [verTodos, setVerTodos] = useState<boolean>(false);
    const [openNuevaLocalidad, setOpenNuevaLocalidad] = useState(false);
    const [saving, setSaving] = useState(false);
    const [provincias, setProvincias] = useState<Provincia[]>([]);
    const [provinciasLoading, setProvinciasLoading] = useState(false);
    const [formData, setFormData] = useState<Localidad>({
        codigoCiudad: 0,
        nombreCiudad: '',
        codigoProvincia: 0,
        codigoRuta: 0,
        codigoPostal: '',
        nombreProvincia: '',
    });

    const handleSearch = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            if (verTodos) setVerTodos(false);
            const trimmed = searchTerm.trim();
            const compact = trimmed.replace(/\s+/g, '');
            
            // Si es numérico puro, buscar directamente por codigoCiudad
            if (compact !== '' && /^\d+$/.test(compact)) {
                try {
                    const localidad = await localidadService.getLocalidad(Number(compact));
                    setLocalidades(localidad ? [localidad] : []);
                    if (!localidad) setError('No se encontraron localidades.');
                } catch (err: any) {
                    setLocalidades([]);
                    setError('No se encontraron localidades.');
                } finally {
                    setLoading(false);
                }
                return;
            }

            const response = await localidadService.getLocalidades({
                searchTerm: trimmed || undefined,
                pageNumber: 1,
                pageSize: 50
            });
            setLocalidades(response.items || []);
            if (!response.items || response.items.length === 0) {
                setError('No se encontraron localidades.');
            }
        } catch (err: any) {
            setError(`Error al buscar localidades: ${err.message || 'Error desconocido'}`);
            setLocalidades([]);
        } finally {
            setLoading(false);
        }
    }, [searchTerm, verTodos]);

    const handleVerTodos = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            setVerTodos(true);
            const pageSize = 1000;
            const first = await localidadService.getLocalidades({
                searchTerm: undefined,
                pageNumber: 1,
                pageSize
            });

            let all: Localidad[] = first.items || [];
            setLocalidades(all);
            const totalPages = first.totalPages || 1;

            if (totalPages > 1) {
                const pages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
                const promises = pages.map((page) =>
                    localidadService
                        .getLocalidades({ searchTerm: undefined, pageNumber: page, pageSize })
                        .then((resp) => {
                            if (resp && Array.isArray(resp.items)) {
                                const items = resp.items as Localidad[];
                                setLocalidades((prev) => prev.concat(items));
                                all = all.concat(items);
                            }
                        })
                        .catch(() => {})
                );
                await Promise.all(promises);
            }
            if (all.length === 0) {
                setError('No se encontraron localidades.');
            }
        } catch (err: any) {
            setError(`Error al cargar localidades: ${err.message || 'Error desconocido'}`);
            setLocalidades([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSelect = (localidad: Localidad) => {
        onSelect(localidad);
        onClose();
    };

    const handleClose = () => {
        setSearchTerm('');
        setLocalidades([]);
        setError(null);
        onClose();
    };

    const handleOpenNuevaLocalidad = () => {
        setFormData({
            codigoCiudad: 0,
            nombreCiudad: '',
            codigoProvincia: 0,
            codigoRuta: 0,
            codigoPostal: '',
            nombreProvincia: '',
        });
        setOpenNuevaLocalidad(true);
    };

    const handleSaveNuevaLocalidad = async () => {
        if (!formData.nombreCiudad?.trim()) {
            enqueueSnackbar('El nombre de la ciudad es requerido', { variant: 'warning' });
            return;
        }
        if (!formData.codigoProvincia) {
            enqueueSnackbar('Debe seleccionar una provincia', { variant: 'warning' });
            return;
        }

        setSaving(true);
        try {
            const nuevaLocalidad = await localidadService.createLocalidad(formData);
            enqueueSnackbar('Localidad creada correctamente', { variant: 'success' });
            setOpenNuevaLocalidad(false);
            // Seleccionar automáticamente la nueva localidad
            handleSelect(nuevaLocalidad);
            // Recargar la lista
            await handleSearch();
        } catch (error: any) {
            enqueueSnackbar(
                error.response?.data?.message || 'Error al crear la localidad',
                { variant: 'error' }
            );
        } finally {
            setSaving(false);
        }
    };

    // Limpia al cerrar y activa búsqueda en vivo con debounce cuando está abierto
    useEffect(() => {
        if (!open) {
            setSearchTerm('');
            setLocalidades([]);
            setError(null);
            return;
        }
        const handle = setTimeout(() => {
            handleSearch();
        }, 400);
        return () => clearTimeout(handle);
    }, [open, searchTerm, handleSearch]);

    useEffect(() => {
        if (!openNuevaLocalidad) return;

        const loadProvincias = async () => {
            try {
                setProvinciasLoading(true);
                const response = await provinciaService.getProvincias({
                    pageNumber: 1,
                    pageSize: 1000
                });
                setProvincias(response.items || []);
            } catch {
                setProvincias([]);
                enqueueSnackbar('No se pudieron cargar las provincias', { variant: 'error' });
            } finally {
                setProvinciasLoading(false);
            }
        };

        loadProvincias();
    }, [openNuevaLocalidad, enqueueSnackbar]);

    return (
        <>
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 0, sm: 2 },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    height: { xs: '100%', sm: 'auto' }
                }
            }}
        >
            <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Buscar Localidad
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Nombre de ciudad, código postal o código de ciudad"
                        inputProps={{ 'aria-label': 'Buscar localidad' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                        size={isSmallScreen ? 'small' : 'medium'}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearch} disabled={loading} edge="end">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ mb: 1.5 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Button 
                            variant="outlined" 
                            onClick={handleSearch}
                            disabled={loading}
                            size={isSmallScreen ? 'medium' : 'small'}
                            fullWidth={isSmallScreen}
                            startIcon={<SearchIcon />}
                        >
                            BUSCAR
                        </Button>
                        <Button 
                            variant={verTodos ? 'contained' : 'text'} 
                            onClick={handleVerTodos}
                            disabled={loading}
                            size={isSmallScreen ? 'medium' : 'small'}
                            fullWidth={isSmallScreen}
                        >
                            VER TODOS
                        </Button>
                    </Box>
                </Box>

                {error && (
                    <Typography color="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {error}
                    </Typography>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {localidades.length > 0 && (
                    <>
                        {isSmallScreen ? (
                            // Vista de tarjetas para móviles
                            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                                <Stack spacing={2}>
                                    {localidades.map((localidad) => (
                                        <Card 
                                            key={localidad.codigoCiudad} 
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    boxShadow: 3
                                                }
                                            }}
                                            onClick={() => handleSelect(localidad)}
                                        >
                                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                    <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                                        Código: {localidad.codigoCiudad}
                                                    </Typography>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(localidad);
                                                        }}
                                                        sx={{ minWidth: 'auto', px: 2 }}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </Box>
                                                <Divider sx={{ my: 1.5 }} />
                                                <Stack spacing={1}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            Ciudad
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {localidad.nombreCiudad?.trim() || '-'}
                                                        </Typography>
                                                    </Box>
                                                    {localidad.nombreProvincia && (
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                Provincia
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                {localidad.nombreProvincia.trim()}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {localidad.codigoPostal && (
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                Código Postal
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                {localidad.codigoPostal.trim()}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            </Box>
                        ) : (
                            // Vista de tabla para pantallas grandes
                            <TableContainer 
                                component={Paper} 
                                sx={{ 
                                    maxHeight: 400,
                                    '& .MuiTableCell-root': {
                                        py: 1.5,
                                        fontSize: '0.875rem'
                                    }
                                }}
                            >
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Código</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Ciudad</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Provincia</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Código Postal</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="center">Acción</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {localidades.map((localidad) => (
                                            <TableRow 
                                                key={localidad.codigoCiudad} 
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => handleSelect(localidad)}
                                            >
                                                <TableCell>{localidad.codigoCiudad}</TableCell>
                                                <TableCell>{localidad.nombreCiudad?.trim() || '-'}</TableCell>
                                                <TableCell>{localidad.nombreProvincia?.trim() || '-'}</TableCell>
                                                <TableCell>{localidad.codigoPostal?.trim() || '-'}</TableCell>
                                                <TableCell align="center">
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(localidad);
                                                        }}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                )}

                {!loading && localidades.length === 0 && searchTerm && (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                        No se encontraron localidades
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                <Button 
                    onClick={handleOpenNuevaLocalidad}
                    fullWidth={isSmallScreen}
                    size={isSmallScreen ? 'medium' : 'small'}
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{ mr: 'auto' }}
                >
                    NUEVA LOCALIDAD
                </Button>
                <Button 
                    onClick={handleClose} 
                    fullWidth={isSmallScreen}
                    size={isSmallScreen ? 'medium' : 'small'}
                    variant="outlined"
                >
                    CANCELAR
                </Button>
            </DialogActions>
        </Dialog>

        {/* Diálogo para crear nueva localidad */}
        <Dialog 
            open={openNuevaLocalidad} 
            onClose={() => setOpenNuevaLocalidad(false)}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Nueva Localidad</Typography>
                    <IconButton onClick={() => setOpenNuevaLocalidad(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Código Ciudad"
                                type="number"
                                value={formData.codigoCiudad || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, codigoCiudad: Number(e.target.value) || 0 }))}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Nombre Ciudad"
                                value={formData.nombreCiudad || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, nombreCiudad: e.target.value }))}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                {...searchableSelectAutocompleteProps}
                                options={provincias}
                                loading={provinciasLoading}
                                value={provincias.find((provincia) => provincia.codigoProvincia === formData.codigoProvincia) || null}
                                onChange={(_, provinciaSeleccionada) =>
                                    setFormData(prev => ({
                                        ...prev,
                                        codigoProvincia: provinciaSeleccionada?.codigoProvincia || 0,
                                        nombreProvincia: provinciaSeleccionada?.nombreProvincia || ''
                                    }))
                                }
                                getOptionLabel={(option) => option.nombreProvincia?.trim() || ''}
                                isOptionEqualToValue={(option, value) => option.codigoProvincia === value.codigoProvincia}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Provincia"
                                        required
                                        fullWidth
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Código Ruta"
                                type="number"
                                value={formData.codigoRuta || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, codigoRuta: Number(e.target.value) || 0 }))}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Código Postal"
                                value={formData.codigoPostal || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, codigoPostal: e.target.value }))}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={() => setOpenNuevaLocalidad(false)}
                    variant="outlined"
                >
                    Cancelar
                </Button>
                <Button 
                    onClick={handleSaveNuevaLocalidad}
                    variant="contained"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} /> : null}
                >
                    Guardar
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default LocalidadSearch;

